/* ES CONFIGURATOR — nucleo logico
 *
 * Interpreta i dati che snippets/es-configurator-data.liquid scrive nella pagina (passi, opzioni, regole di
 * risoluzione con le matrici delle varianti) e risponde alle domande dell'interfaccia: quali passi e opzioni si
 * vedono, quali si possono scegliere e perché no, quanto costa, cosa va nel carrello.
 *
 * Non tocca il DOM (gira anche in Node) e non contiene regole di prodotto: nessun nome, valore o prezzo è scritto
 * qui, tutto arriva dai metaobject. Semantica dei dati e API: docs/configuratore-motore.md.
 */

const VERSIONE_CODICE = '1'; // primo carattere del codice ?c=
const LIMITE_TESTO = 200; // caratteri di una risposta libera, anche da un codice; un passo può chiederne meno (lunghezza_max)
const LIMITE_CODICE = 8000; // un codice più lungo non viene nemmeno letto
const PROFONDITA_RAMI = 3; // rami dentro rami: oltre è un errore di dati

/* ================================================================================================
   TESTI E CONDIZIONI
   ================================================================================================ */

const minuscolo = (testo) => String(testo ?? '').trim().normalize('NFC').toLowerCase();

/* Per ritrovare una risposta fra i valori a catalogo: minuscole, senza accenti, solo lettere e cifre. */
const normalizzato = (testo) => minuscolo(testo).normalize('NFD').replace(/[^a-z0-9]/g, '');

/* Un surrogato UTF-16 spaiato (mezza emoji) diventa «�»: encodeURIComponent lo rifiuterebbe con un'eccezione. */
const carattereIntero = (c) => (c.length === 1 && c >= '\uD800' && c <= '\uDFFF' ? '�' : c);

/* Una risposta libera: senza spazi ai lati e al massimo `limite` caratteri (quello del passo, vedi limiteTesto).
 * Si conta per caratteri (Array.from) e non per unità UTF-16, così il taglio non spezza un'emoji. */
function testoLibero(testo, limite = LIMITE_TESTO) {
  return Array.from(String(testo ?? '').trim(), carattereIntero).slice(0, limite).join('').trim();
}

/* `lunghezza_max` di un passo: un intero da 1 a LIMITE_TESTO. Assente o vuoto → null (vale LIMITE_TESTO); qualunque
 * altra cosa (0, 10.5, 300, «dieci») → NaN, cioè non valido: vale LIMITE_TESTO, con un avviso di errore. */
function lunghezzaMassima(valore) {
  if (valore === undefined || valore === null || (typeof valore === 'string' && !valore.trim())) return null;
  const n = typeof valore === 'number' || typeof valore === 'string' ? Number(valore) : NaN;
  return Number.isInteger(n) && n >= 1 && n <= LIMITE_TESTO ? n : NaN;
}

/* Quanti caratteri tiene la risposta a un passo: il suo `limite` (letto dal modello), altrimenti LIMITE_TESTO. */
const limiteTesto = (passo) => passo?.limite || LIMITE_TESTO;

/* chiave=valore: lettere (anche accentate), cifre, `_`, `-` e `.`; nel valore anche `+`. */
const COPPIA = /^\s*([\p{L}\p{N}_.-]+)\s*=\s*([\p{L}\p{N}_.+-]+)\s*$/u;

/* `chiave=valore,chiave=valore` (tutte vere) → [[chiave, valore], …]; vuota → [] (sempre vera).
 * Qualunque altra cosa (`!=`, `a|b`, `>`, spazi dentro un valore…) → null: non interpretabile. */
function leggiCondizione(testo) {
  const coppie = minuscolo(testo).split(',').filter((pezzo) => pezzo.trim()).map((pezzo) => COPPIA.exec(pezzo));
  return coppie.every(Boolean) ? coppie.map((c) => [c[1], c[2]]) : null;
}

/* Una condizione non interpretabile vale sempre (fail-open): un refuso non deve far sparire niente. */
const condizioneVera = (condizione, contesto) => condizione === null || condizione.every(([k, v]) => contesto.get(k)?.has(v));

function registra(contesto, chiave, valore) {
  [chiave, valore] = [minuscolo(chiave), minuscolo(valore)];
  if (chiave && valore) (contesto.get(chiave) || contesto.set(chiave, new Set()).get(chiave)).add(valore);
}

/* mappa_variante: `Nome=Valore|Alternativa|Inizio*,Nome={chiave-passo}` → [{nome, valori: […], prefissi: […]} |
 * {nome, segnaposto}]. Un valore che finisce con `*` vale per tutti i valori a catalogo che cominciano così, confrontati
 * normalizzati (`Modello=Ktm*`: la marca del portatarga, 2026-10-03). */
function leggiMappa(testo, segnala) {
  const vincoli = [];
  for (const pezzo of String(testo || '').split(',')) {
    const uguale = pezzo.indexOf('='), nome = minuscolo(pezzo.slice(0, uguale)), valore = pezzo.slice(uguale + 1).trim();
    const segnaposto = /^\{(.+)\}$/.exec(valore);
    if (uguale > 0 && nome && valore) {
      if (segnaposto) vincoli.push({ nome, segnaposto: minuscolo(segnaposto[1]) });
      else {
        const alternative = valore.split('|').map(minuscolo).filter(Boolean);
        const prefissi = alternative.filter((a) => a.endsWith('*')).map((a) => normalizzato(a.slice(0, -1))).filter(Boolean);
        vincoli.push({ nome, valori: alternative.filter((a) => !a.endsWith('*')), ...(prefissi.length && { prefissi }) });
      }
    } else if (pezzo.trim()) segnala('mappa_variante illeggibile', pezzo);
  }
  return vincoli;
}

/* Un valore a catalogo (già in minuscolo, come nelle colonne della matrice) rispetta un vincolo fisso: è fra i suoi
 * valori, oppure comincia con uno dei suoi prefissi. */
const rispetta = (vincolo, valore) =>
  vincolo.valori.includes(valore) || !!vincolo.prefissi?.some((p) => normalizzato(valore).startsWith(p));

/* ================================================================================================
   MATRICE DELLE VARIANTI
   Liquid la scrive in colonne parallele: varianti.id[i], .prezzo[i], .opzione1[i]…
   ================================================================================================ */

/* Un prezzo mancante resta ignoto (null), mai 0: un totale con un prezzo ignoto non si mostra. */
const prezzoNoto = (valore) => (Number.isFinite(valore) ? valore : null);

function preparaMatrice(prodotto, segnala) {
  if (!prodotto?.varianti || prodotto.matrice) return;
  const V = prodotto.varianti, quante = Array.isArray(V.id) ? V.id.length : 0;
  const colonne = [1, 2, 3].map((n) => Array.from({ length: quante }, (_, i) => minuscolo(V['opzione' + n]?.[i])));
  // per ogni colonna: valore normalizzato → valori a catalogo che gli corrispondono (più di uno: ambiguo, vedi
  // controllaSegnaposto)
  const perNormale = colonne.map((colonna) => {
    const indice = new Map();
    for (const valore of new Set(colonna)) {
      const chiave = normalizzato(valore);
      if (chiave) (indice.get(chiave) || indice.set(chiave, []).get(chiave)).push(valore);
    }
    return indice;
  });
  prodotto.matrice = { nomi: (prodotto.nomi_opzioni || []).map(minuscolo), colonne, perNormale, quante };
  if (prodotto.varianti_totali && prodotto.varianti_totali !== quante) segnala('matrice delle varianti incompleta', prodotto.titolo);
}

/* Il nome di una dimensione (colonna j) come lo scrive Shopify; `opzioneN` se Liquid non l'ha passato. */
const nomeDimensione = (prodotto, j) => prodotto.nomi_opzioni?.[j] || 'opzione' + (j + 1);

/* Il valore a catalogo di una risposta: prima per chiave dell'opzione, poi per etichetta. L'etichetta è quella
 * della lingua predefinita (testiPredefiniti), la stessa del catalogo: quella a schermo può essere tradotta. */
function valoriACatalogo(matrice, colonna, risposte) {
  const cerca = (testo) => matrice.perNormale[colonna].get(normalizzato(testo)) || [];
  return risposte.flatMap((risposta) => {
    const perChiave = cerca(risposta.chiave);
    return perChiave.length ? perChiave : cerca(risposta.etichetta);
  });
}

/* Le varianti che rispettano i vincoli, e le colonne vincolate. Un vincolo su un nome d'opzione che il prodotto
 * non ha si ignora (così `Colore=Nero,Color=Nero` funziona su prodotti che chiamano l'opzione in modi diversi). */
function compatibili(prodotto, vincoli) {
  const M = prodotto?.matrice;
  if (!M) return { indici: [], vincolate: [] };
  const attivi = [];
  for (const vincolo of vincoli) {
    const j = M.nomi.indexOf(vincolo.nome);
    if (j < 0) continue;
    if (vincolo.valori) attivi.push([j, (valore) => rispetta(vincolo, valore)]);
    else {
      const valori = valoriACatalogo(M, j, vincolo.risposte);
      attivi.push([j, (valore) => valori.includes(valore)]);
    }
  }
  const indici = [];
  for (let i = 0; i < M.quante; i++) if (attivi.every(([j, va]) => va(M.colonne[j][i]))) indici.push(i);
  return { indici, vincolate: attivi.map(([j]) => j) };
}

/* Le colonne su cui le varianti `indici` hanno ancora valori diversi. */
const colonneLibere = (prodotto, indici) =>
  [0, 1, 2].filter((j) => indici.some((i) => prodotto.matrice.colonne[j][i] !== prodotto.matrice.colonne[j][indici[0]]));

/* A parità di tutto il resto si preferisce la variante disponibile, poi quella che costa meno (un prezzo ignoto
 * conta come il più alto). */
const preferibile = (a, b) => (a.disponibile !== b.disponibile ? a.disponibile : (a.prezzo ?? Infinity) < (b.prezzo ?? Infinity));

/* La più conveniente fra le varianti `indici` (a parità resta la prima trovata), partendo da `attuale`. */
function migliorVariante(prodotto, indici, attuale = null) {
  const V = prodotto.varianti;
  let migliore = attuale;
  for (const indice of indici) {
    const candidata = { prodotto, indice, prezzo: prezzoNoto(V.prezzo?.[indice]), disponibile: V.disponibile?.[indice] !== false };
    if (!migliore || preferibile(candidata, migliore)) migliore = candidata;
  }
  return migliore;
}

function varianteDa(prodotto, i) {
  const V = prodotto.varianti;
  return {
    id: V.id[i],
    prezzo: prezzoNoto(V.prezzo?.[i]),
    disponibile: V.disponibile?.[i] !== false,
    sku: V.sku?.[i] || '',
    // il titolo a schermo: in un'altra lingua quello tradotto (fondiLingue), mentre le colonne restano del catalogo
    titolo: V.titolo?.[i] ?? [V.opzione1, V.opzione2, V.opzione3].map((c) => c?.[i]).filter((x) => x != null && x !== '').join(' / '),
    prodotto: prodotto.titolo,
    url: prodotto.url,
    immagine: prodotto.immagini?.[V.immagine?.[i] - 1] || prodotto.immagine || null
  };
}

/* La variante predefinita di un prodotto_aggiuntivo senza mappa (quella che Liquid indica con variante_id). */
function variantePredefinita(prodotto) {
  const i = prodotto.varianti?.id?.indexOf(prodotto.variante_id) ?? -1;
  if (i >= 0) return varianteDa(prodotto, i);
  return {
    id: prodotto.variante_id,
    prezzo: prezzoNoto(prodotto.prezzo),
    disponibile: prodotto.disponibile !== false,
    sku: prodotto.sku || '',
    titolo: '',
    prodotto: prodotto.titolo,
    url: prodotto.url,
    immagine: prodotto.immagine || null
  };
}

/* La foto di un prodotto deciso ma forse non ancora univoco (A5): quella della variante se è una sola o se le
 * varianti rimaste la condividono, altrimenti la foto principale. Mai quella di una variante non scelta. */
function fotoDelProdotto(prodotto, indici) {
  if (indici.length === 1) return varianteDa(prodotto, indici[0]).immagine;
  const numeri = new Set(indici.map((i) => prodotto.varianti.immagine?.[i]));
  return (numeri.size === 1 && prodotto.immagini?.[[...numeri][0] - 1]) || prodotto.immagine || null;
}

/* La foto di ciò che una scelta farebbe comprare, fra le varianti `indici` ancora possibili: { url, certa }.
 * certa  le varianti hanno tutte la stessa foto (o ce n'è una sola): è la foto di ciò che arriverà a casa
 * altrimenti quella della variante più conveniente (lo stesso criterio delle stime): un esempio fedele alla scelta,
 * non la promessa di una variante. */
function fotoDiVarianti(prodotto, indici) {
  if (!prodotto?.varianti || !indici.length) return { url: prodotto?.immagine || null, certa: false };
  const numeri = new Set(indici.map((i) => prodotto.varianti.immagine?.[i]));
  const indice = numeri.size === 1 ? indici[0] : migliorVariante(prodotto, indici).indice;
  return { url: varianteDa(prodotto, indice).immagine, certa: numeri.size === 1 };
}

/* C1: la variante c'è solo se è univoca. Altrimenti restano le dimensioni libere, la stima (minimo) e un esempio. */
function esitoMatrice(prodotto, indici, migliore = migliorVariante(prodotto, indici)) {
  return {
    prodotto,
    indici,
    variante: indici.length === 1 ? varianteDa(prodotto, indici[0]) : null,
    libere: indici.length > 1 ? colonneLibere(prodotto, indici).map((j) => nomeDimensione(prodotto, j)) : [],
    minimo: migliore ? migliore.prezzo : null,
    disponibile: !!migliore?.disponibile,
    esempio: migliore ? varianteDa(migliore.prodotto, migliore.indice) : null
  };
}

/* Le chiavi dei passi che la mappa di un accessorio richiama con un segnaposto (`Colore={pb-colore}`): le risposte
 * a quei passi scelgono la variante dell'accessorio, e con essa la sua foto. Calcolate una volta per modello. */
const RICHIAMATI = new WeakMap();
function passiRichiamati(modello) {
  if (!RICHIAMATI.has(modello)) {
    const chiavi = modello.opzioni.flatMap((o) => (o.prodotto ? o.vincoli.map((v) => v.segnaposto).filter(Boolean) : []));
    RICHIAMATI.set(modello, new Set(chiavi));
  }
  return RICHIAMATI.get(modello);
}

/* Le chiavi che le regole di ogni spazio leggono nella `condizione`. Calcolate una volta per modello. */
const LETTE = new WeakMap();
function chiaviDelleRegole(modello) {
  if (!LETTE.has(modello)) {
    const perSpazio = new Map();
    for (const r of modello.regole) {
      const chiavi = perSpazio.get(r.spazio) || perSpazio.set(r.spazio, new Set()).get(r.spazio);
      for (const [chiave] of r.quando) chiavi.add(chiave);
    }
    LETTE.set(modello, perSpazio);
  }
  return LETTE.get(modello);
}

/* L'opzione decide QUALE prodotto vende il suo spazio, anche senza mappa: il suo passo o un suo `imposta` scrive una
 * chiave che le regole dello spazio leggono (il design o i LED del faro, 2026-10-01). */
function decideRegola(modello, opzione) {
  const lette = chiaviDelleRegole(modello).get(spazioVincolato(modello, opzione));
  return !!lette && [minuscolo(opzione.suoPasso?.chiave), ...opzione.imposte.map(([chiave]) => chiave)].some((k) => lette.has(k));
}

/* ================================================================================================
   PROPRIETÀ D'ORDINE, CODICE, AVVISI
   ================================================================================================ */

/* I testi di una voce nella lingua predefinita del negozio, quella dell'ordine e del catalogo. In una pagina in
 * un'altra lingua fondiLingue li mette in `predefinita` (i testi a schermo restano tradotti); altrimenti sono
 * quelli della voce. */
const testiPredefiniti = (voce) => voce.predefinita || voce;

/* Nome della proprietà di un passo e testo di una risposta: a schermo nella lingua della pagina; con `ordine`
 * nella lingua predefinita, così chi assembla legge sempre la stessa lingua. */
const nomeProprieta = (passo, ordine = false) => {
  const testi = ordine ? testiPredefiniti(passo) : passo;
  return testi.etichetta_ordine || testi.titolo || passo.chiave;
};

const testoRisposta = (passo, valori, ordine = false) =>
  passo.tipo === 'testo'
    ? valori[0]
    : valori.map((k) => {
      const o = passo.opzioni.find((x) => x.chiave === k);
      return (o && (ordine ? testiPredefiniti(o) : o).etichetta) || k;
    }).join(', ');

/* Un'opzione che ha una riga propria nel carrello, o che apre un ramo che ne avrà. */
const produceRighe = (opzione) => !!(opzione.variante || opzione.prodotto || opzione.apre_ramo);

/* Lo spazio che le mappe di un'opzione vincolano: quello del suo passo, oppure (C1) quello del ramo che apre, se è
 * una porta senza prodotto proprio verso uno spazio con regole. */
function spazioVincolato(modello, opzione) {
  const portaVersoProdotto = opzione.apre_ramo && !opzione.prodotto && modello.spaziConRegole.has(opzione.apre_ramo);
  return portaVersoProdotto ? opzione.apre_ramo : opzione.suoPasso?.spazio;
}

function fnv1a(testo) {
  let h = 0x811c9dc5;
  for (let i = 0; i < testo.length; i++) h = Math.imul(h ^ testo.charCodeAt(i), 16777619) >>> 0;
  return h.toString(36);
}

/* Ogni avviso si registra una volta in modello.avvisi (testo → errore?). In console vanno solo gli errori
 * di dati; il resto (situazioni normali, curiosità dei dati) solo con il debug acceso. */
function avvisa(modello, errore, messaggio, dettaglio) {
  const testo = messaggio + (dettaglio === undefined ? '' : ' ' + JSON.stringify(dettaglio));
  if (modello.avvisi.has(testo)) return;
  modello.avvisi.set(testo, !!errore);
  if (errore || modello.debug) console.warn('[es-configurator] ' + testo);
}

/* ================================================================================================
   MODELLO — legge i dati una volta sola
   ================================================================================================ */

export function modello(dati = {}, debug = false) {
  const m = { ...dati, debug: !!debug, avvisi: new Map(), rami: {} };
  const errore = (messaggio, dettaglio) => avvisa(m, true, messaggio, dettaglio);
  const voci = (lista) => (Array.isArray(lista) ? lista : []).filter((voce) => voce && typeof voce === 'object');
  const ordina = (a, b) => (a.ordine || 0) - (b.ordine || 0) || String(a.chiave).localeCompare(String(b.chiave));
  const leggi = (voce, campo, conseguenza) => {
    const condizione = leggiCondizione(voce[campo]);
    if (condizione === null) errore(campo + ' non interpretabile: ' + conseguenza, [voce.handle || voce.chiave, voce[campo]]);
    return condizione;
  };

  m.opzioni = voci(dati.opzioni);
  const opzioniPerPasso = {};
  for (const o of m.opzioni) {
    o.quando = leggi(o, 'mostra_se', "l'opzione si mostra");
    o.imposte = leggi(o, 'imposta', 'ignorato') || [];
    o.vincoli = leggiMappa(o.mappa_variante, errore);
    preparaMatrice(o.prodotto, errore);
    preparaMatrice(o.base, errore);
    if (Number(o.prezzo_delta)) errore('prezzo_delta non addebitabile sul piano Basic: ignorato', o.handle);
    (opzioniPerPasso[o.spazio + '/' + o.passo] ||= []).push(o);
  }

  m.passi = voci(dati.passi).sort(ordina);
  const spazioDellaChiave = new Map();
  m.passi.forEach((p, indice) => {
    p.indice = indice;
    p.tipo = minuscolo(p.tipo) || 'singola';
    p.quando = leggi(p, 'mostra_se', 'il passo si mostra');
    // lunghezza_max vale solo sui passi testo: `lunghezza_max` resta l'intero scritto in admin, se valido (null se
    // manca o non vale: l'interfaccia mostra il contatore solo se c'è); `limite` è quello che vale davvero
    const lunghezza = lunghezzaMassima(p.lunghezza_max);
    if (lunghezza !== null && p.tipo !== 'testo') avvisa(m, false, 'lunghezza_max su un passo che non è testo: ignorata', p.handle || p.chiave);
    else if (Number.isNaN(lunghezza)) errore('lunghezza_max non valida (un intero da 1 a ' + LIMITE_TESTO + '): vale ' + LIMITE_TESTO, [p.handle || p.chiave, p.lunghezza_max]);
    p.lunghezza_max = p.tipo === 'testo' && lunghezza > 0 ? lunghezza : null;
    p.limite = p.tipo === 'testo' ? p.lunghezza_max || LIMITE_TESTO : null;
    p.opzioni = (opzioniPerPasso[p.spazio + '/' + p.chiave] || []).slice();
    // opzioni_di: un passo senza opzioni proprie mostra quelle di un altro passo (una tavolozza ripetuta, curata in un
    // punto solo). Sono copie: ognuna appartiene al passo che la mostra, con le sue risposte e le sue condizioni. Il
    // passo d'origine si cerca nello stesso spazio, poi negli altri; niente catene (l'origine deve avere opzioni sue)
    const origine = String(p.opzioni_di ?? '').trim();
    if (origine && p.opzioni.length) avvisa(m, false, 'opzioni_di su un passo con opzioni proprie: ignorato', p.handle || p.chiave);
    else if (/^variante:/i.test(origine)) p.daVarianti = minuscolo(origine.slice(origine.indexOf(':') + 1)); // dopo le regole
    else if (origine) {
      const prestate = opzioniPerPasso[p.spazio + '/' + origine] || Object.entries(opzioniPerPasso).find(([k]) => k.endsWith('/' + origine))?.[1];
      if (!prestate?.length) errore('opzioni_di: il passo indicato non ha opzioni proprie', [p.handle || p.chiave, origine]);
      else {
        p.opzioni = prestate.map((o) => ({ ...o, passo: p.chiave, spazio: p.spazio }));
        m.opzioni.push(...p.opzioni);
      }
    }
    p.opzioni.sort(ordina);
    // riferimento all'indietro non enumerabile: gli oggetti restituiti all'interfaccia restano serializzabili
    for (const o of p.opzioni) Object.defineProperty(o, 'suoPasso', { value: p, configurable: true, writable: true });
    if (p.spazio !== m.configuratore) (m.rami[p.spazio] ||= []).push(p);
    // le risposte sono indicizzate per chiave del passo: due passi con la stessa chiave condividono la risposta
    if (spazioDellaChiave.has(p.chiave) && spazioDellaChiave.get(p.chiave) !== p.spazio) {
      avvisa(m, false, 'passi con la stessa chiave in spazi diversi: condividono la risposta', p.chiave);
    }
    spazioDellaChiave.set(p.chiave, p.spazio);
  });
  m.principali = m.passi.filter((p) => p.spazio === m.configuratore);

  m.regole = voci(dati.risoluzioni).filter((r) => {
    r.quando = leggiCondizione(r.condizione);
    r.vincoli = leggiMappa(r.mappa_variante, errore);
    preparaMatrice(r, errore);
    if (!r.quando?.length) errore('regola senza condizione leggibile: ignorata', r.handle);
    else if (!r.matrice) errore('regola senza varianti: ignorata', r.handle);
    return r.quando?.length > 0 && !!r.matrice;
  });
  m.spaziConRegole = new Set(m.regole.map((r) => r.spazio));
  for (const p of m.passi) if (p.daVarianti) opzioniDaVarianti(m, p, errore);

  // le chiavi che un passo o un `imposta` sanno decidere (le altre non si aprono mai: vedi _possibili)
  m.chiaviProdotte = new Set(['configuratore']);
  for (const p of m.passi) {
    m.chiaviProdotte.add(minuscolo(p.chiave));
    for (const o of p.opzioni) for (const [chiave] of o.imposte) m.chiaviProdotte.add(chiave);
  }

  // una condizione su una chiave che niente produce non varrà mai: è un refuso, oppure una chiave dell'altro
  // configuratore (voluto). Non si può distinguere, quindi è un'informazione e non un errore.
  for (const voce of [...m.passi, ...m.passi.flatMap((p) => p.opzioni), ...m.regole]) {
    const ignote = (voce.quando || []).map(([chiave]) => chiave).filter((chiave) => !m.chiaviProdotte.has(chiave));
    if (!ignote.length) continue;
    avvisa(m, false, 'condizione su chiavi che nessun passo né imposta produce: non varrà mai', [voce.handle || voce.chiave, ...ignote]);
  }

  controllaRami(m, errore);
  controllaMappe(m, errore);
  return m;
}

/* `opzioni_di: variante:Modello` (2026-10-03, i modelli moto del portatarga): le opzioni del passo sono i valori di
 * quell'opzione variante nei prodotti delle regole del suo spazio, nell'ordine del catalogo. Un valore nuovo a catalogo
 * è un'opzione nuova, senza metaobject (non pesa sul tetto delle 250). Ogni opzione fissa la variante su quel valore;
 * nel percorso si vedono solo quelle che le risposte date prima permettono ancora (opzioniAmmesse), e con una sola il
 * passo non si mostra: la dimensione è già decisa (la marca «Universale» ha un solo modello). Un valore uguale in due
 * prodotti è un'opzione sola. */
const chiaveDa = (valore) => minuscolo(valore).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function opzioniDaVarianti(m, p, errore) {
  const perChiave = new Map();
  for (const r of m.regole.filter((x) => x.spazio === p.spazio)) {
    const j = r.matrice.nomi.indexOf(p.daVarianti);
    if (j < 0) continue;
    const originali = r.varianti['opzione' + (j + 1)] || [];
    r.matrice.colonne[j].forEach((valore, i) => {
      const chiave = chiaveDa(valore);
      if (!chiave) return;
      const o = perChiave.get(chiave) || perChiave.set(chiave, {
        spazio: p.spazio, passo: p.chiave, handle: `${p.handle || p.chiave}/${chiave}`, chiave, ordine: perChiave.size,
        etichetta: String(originali[i] ?? valore).trim(), quando: [], imposte: [], vincoli: [{ nome: p.daVarianti, valori: [] }],
        dimensione: p.daVarianti
      }).get(chiave);
      if (!o.vincoli[0].valori.includes(valore)) o.vincoli[0].valori.push(valore);
    });
  }
  if (!perChiave.size) errore('opzioni_di: nessun prodotto delle regole ha questa opzione variante', [p.handle || p.chiave, p.daVarianti]);
  p.opzioni = [...perChiave.values()];
  for (const o of p.opzioni) Object.defineProperty(o, 'suoPasso', { value: p, configurable: true, writable: true });
  m.opzioni.push(...p.opzioni);
}

/* Le chiavi delle opzioni di un passo `daVarianti` ancora possibili: i valori della sua dimensione fra le varianti che
 * rispettano le mappe delle scelte fatte prima (`scelte`) nelle regole già soddisfatte dello spazio (tutte, finché
 * nessuna lo è). */
function opzioniAmmesse(m, passo, contesto, scelte) {
  const regole = m.regole.filter((r) => r.spazio === passo.spazio);
  const soddisfatte = regole.filter((r) => condizioneVera(r.quando, contesto));
  // fra le soddisfatte vale la più specifica, come per il prodotto (§6): con 50cc + Scooter il 50cc non presta i suoi modelli
  const piu = Math.max(0, ...soddisfatte.map((r) => r.quando.length));
  const valide = soddisfatte.filter((r) => r.quando.length === piu);
  const vincoli = scelte.filter((o) => !o.prodotto && spazioVincolato(m, o) === passo.spazio).flatMap((o) => o.vincoli).filter((v) => v.valori);
  const ammesse = new Set();
  for (const r of valide.length ? valide : regole) {
    const j = r.matrice.nomi.indexOf(passo.daVarianti);
    if (j >= 0) for (const i of compatibili(r, [...r.vincoli, ...vincoli]).indici) ammesse.add(chiaveDa(r.matrice.colonne[j][i]));
  }
  return ammesse;
}

/* I prodotti di cui una voce (opzione o regola) vincola la variante con la sua mappa_variante. */
function prodottiVincolati(m, voce) {
  if (voce.prodotto?.matrice) return [voce.prodotto];
  if (voce.matrice) return [voce];
  const spazio = spazioVincolato(m, voce);
  return [...m.regole.filter((r) => r.spazio === spazio), ...(voce.base?.matrice ? [voce.base] : [])];
}

/* Un ramo che, direttamente o attraverso altri rami, riapre sé stesso è un errore di dati: nel percorso il motore
 * lo inserisce una volta sola (vedi _calcola). */
function controllaRami(m, errore) {
  const porteDi = (spazio) => (m.rami[spazio] || []).flatMap((p) => p.opzioni.map((o) => o.apre_ramo).filter(Boolean));
  for (const ramo of Object.keys(m.rami)) {
    const visti = new Set(), daVisitare = porteDi(ramo);
    while (daVisitare.length) {
      const spazio = daVisitare.pop();
      if (spazio === ramo) {
        errore('rami annidati in ciclo (un ramo riapre sé stesso): il ramo compare una volta sola', ramo);
        break;
      }
      if (!visti.has(spazio)) daVisitare.push(...porteDi(spazio));
      visti.add(spazio);
    }
  }
}

/* Diagnostica sulle mappe_variante, per ogni prodotto che vincolano:
 *  - un valore fisso che nessuno dei prodotti vincolati ha a catalogo (un refuso come `KIT=Kit bse`) rende
 *    l'opzione impossibile ovunque. Basta che un prodotto lo abbia: le alternative con `|` servono proprio a
 *    prodotti diversi;
 *  - un'opzione del passo richiamato da un segnaposto che si ritrova a catalogo solo per etichetta funziona in
 *    italiano e si rompe con le etichette tradotte: va allineata la chiave;
 *  - due valori a catalogo che normalizzati coincidono («1.3 Ah» e «13 Ah») un segnaposto non li distingue: la
 *    variante resterebbe indeterminata. */
function controllaMappe(m, errore) {
  const voci = [...m.passi.flatMap((p) => p.opzioni), ...m.regole];
  for (const voce of voci) {
    for (const vincolo of voce.vincoli.filter((v) => v.valori)) {
      const colonne = prodottiVincolati(m, voce)
        .map((prodotto) => prodotto.matrice.colonne[prodotto.matrice.nomi.indexOf(vincolo.nome)])
        .filter(Boolean);
      if (colonne.length && !colonne.some((colonna) => colonna.some((valore) => rispetta(vincolo, valore)))) {
        const dove = [voce.handle, vincolo.nome, ...vincolo.valori, ...(vincolo.prefissi || []).map((p) => p + '*')];
        errore('mappa_variante con un valore che nessun prodotto ha a catalogo: nessuna variante corrisponde', dove);
      }
    }
    for (const vincolo of voce.vincoli.filter((v) => v.segnaposto)) {
      for (const prodotto of prodottiVincolati(m, voce)) {
        const j = prodotto.matrice.nomi.indexOf(vincolo.nome);
        if (j < 0) continue;
        for (const valori of prodotto.matrice.perNormale[j].values()) {
          if (valori.length < 2) continue;
          const dove = [voce.handle, prodotto.titolo, vincolo.nome, ...valori];
          errore('valori a catalogo che coincidono una volta normalizzati: il segnaposto non li distingue', dove);
        }
        for (const passo of m.passi.filter((q) => minuscolo(q.chiave) === vincolo.segnaposto && q.tipo !== 'testo')) {
          for (const o of passo.opzioni) {
            // un'opzione senza valore a catalogo per questo prodotto è lecita (la si nasconde con mostra_se)
            if (valoriACatalogo(prodotto.matrice, j, [{ chiave: o.chiave }]).length) continue;
            if (!valoriACatalogo(prodotto.matrice, j, [{ etichetta: testiPredefiniti(o).etichetta }]).length) continue;
            const dove = [voce.handle, prodotto.titolo, vincolo.nome, o.chiave];
            errore('segnaposto ritrovato solo per etichetta (si romperà con le traduzioni): allinea la chiave al catalogo', dove);
          }
        }
      }
    }
  }
}

/* ================================================================================================
   LINGUE — una pagina in un'altra lingua, fusa con i dati della lingua predefinita
   ------------------------------------------------------------------------------------------------
   Nelle lingue diverse dalla predefinita Shopify scrive tradotti anche i nomi e i valori delle opzioni varianti
   dei prodotti (e le traduzioni del catalogo possono essere sbagliate): le mappe e le regole, scritte nella lingua
   predefinita, non troverebbero più le varianti. L'interfaccia scarica allora anche i dati della stessa sezione
   nella lingua predefinita e li fonde con quelli della pagina, voce per voce, abbinate per handle:
     dalla lingua predefinita  tutto ciò che risolve: chiavi, tipi, obblighi, condizioni, `imposta`, mappe, porte,
                               spazi, regole, nomi e valori delle opzioni di ogni matrice, id delle varianti;
                               e, in `predefinita`, i testi che vanno nell'ordine (titolo, etichetta_ordine,
                               etichetta): chi assembla legge sempre la stessa lingua
     dalla pagina              i testi per il cliente (titolo, sottotitolo, nota, etichetta_ordine, esempio, etichetta,
                               descrizione, badge, alt delle foto, inclusi, titoli e URL di prodotti e varianti),
                               prezzi e disponibilità (lingua e mercato correnti), valuta e rotte
   Un campo tecnico tradotto per sbaglio in admin non conta: si legge solo quello della lingua predefinita.
   Se le due versioni non combaciano (una voce, un prodotto o una variante che c'è da una parte sola, id diversi,
   dati che non sono davvero nella lingua predefinita) non si fonde niente: meglio nessun configuratore che un
   acquisto sbagliato. Restituisce { dati, errori }: `dati` è null se c'è almeno un errore.
   ================================================================================================ */

/* I campi di un prodotto venduto (variante, accessorio, prodotto base, regola) che arrivano dalla pagina. */
const DALLA_PAGINA = ['prezzo', 'disponibile', 'titolo', 'prodotto', 'url', 'immagine'];

export function fondiLingue(pagina, predefinita) {
  const errori = [];
  const errore = (testo, dove) => errori.push(testo + ' ' + JSON.stringify(dove));
  const voci = (lista) => (Array.isArray(lista) ? lista.filter((voce) => voce && typeof voce === 'object') : []);

  if (!pagina || !predefinita) return { dati: null, errori: ['dati mancanti'] };
  if (predefinita.lingua?.predefinita !== true) errore('i dati da fondere non sono nella lingua predefinita', predefinita.lingua);
  for (const campo of ['configuratore', 'versione']) {
    if (pagina[campo] !== predefinita[campo]) errore(campo + ' diverso', [pagina[campo], predefinita[campo]]);
  }

  // [voce della lingua predefinita, stessa voce nella pagina]: stesse voci da tutte e due le parti
  const abbina = (tipo) => {
    const qui = voci(pagina[tipo]), la = voci(predefinita[tipo]), perHandle = new Map(qui.map((v) => [v.handle, v]));
    if (qui.length !== la.length || perHandle.size !== qui.length) errore('voci diverse', [tipo, qui.length, la.length]);
    return la.map((voce) => {
      if (!perHandle.has(voce.handle)) errore('voce assente nella lingua della pagina', [tipo, voce.handle]);
      return [voce, perHandle.get(voce.handle) || voce];
    });
  };

  // Le varianti di una matrice: colonne della lingua predefinita; prezzo, disponibilità e titolo dalla pagina,
  // variante per variante secondo l'id.
  const matrice = (V, T, dove) => {
    const posizione = new Map((T?.id || []).map((id, j) => [id, j])), ids = V.id || [];
    if (ids.length !== posizione.size || ids.some((id) => !posizione.has(id))) {
      errore('varianti diverse', dove);
      return V;
    }
    const j = ids.map((id) => posizione.get(id));
    const titolo = j.map((k) => [T.opzione1, T.opzione2, T.opzione3].map((c) => c?.[k]).filter((x) => x != null && x !== '').join(' / '));
    return { ...V, prezzo: j.map((k) => T.prezzo?.[k]), disponibile: j.map((k) => T.disponibile?.[k]), titolo };
  };

  // Un prodotto venduto: lo stesso nelle due lingue (stessi id), con i campi della pagina.
  const venduto = (voce, tradotto, ids, dove) => {
    if (!voce && !tradotto) return voce;
    if (!voce || !tradotto || ids.some((id) => voce[id] !== tradotto[id])) {
      errore('prodotto diverso', dove);
      return voce;
    }
    const fuso = { ...voce };
    for (const campo of DALLA_PAGINA) if (campo in voce) fuso[campo] = tradotto[campo];
    if (voce.varianti) fuso.varianti = matrice(voce.varianti, tradotto.varianti, dove);
    return fuso;
  };

  // i testi a schermo della pagina; se un testo manca nella pagina resta quello della lingua predefinita
  const testi = (voce, tradotta, campi) => Object.fromEntries(campi.map((campo) => [campo, tradotta[campo] ?? voce[campo]]));

  const passi = abbina('passi').map(([voce, tradotta]) => ({
    ...voce,
    ...testi(voce, tradotta, ['titolo', 'sottotitolo', 'nota', 'etichetta_ordine', 'esempio']),
    predefinita: { titolo: voce.titolo, etichetta_ordine: voce.etichetta_ordine }
  }));

  const opzioni = abbina('opzioni').map(([voce, tradotta]) => {
    const dove = ['opzione', voce.handle], fusa = { ...voce, ...testi(voce, tradotta, ['etichetta', 'descrizione', 'badge', 'inclusi']) };
    if (voce.immagine) fusa.immagine = { ...voce.immagine, alt: tradotta.immagine?.alt ?? voce.immagine.alt };
    for (const [campo, ids] of [['variante', ['id']], ['prodotto', ['id', 'variante_id']], ['base', ['prodotto_id']]]) {
      if (voce[campo] || tradotta[campo]) fusa[campo] = venduto(voce[campo], tradotta[campo], ids, dove);
    }
    fusa.predefinita = { etichetta: voce.etichetta };
    return fusa;
  });

  const risoluzioni = abbina('risoluzioni').map(([voce, tradotta]) => venduto(voce, tradotta, ['prodotto_id'], ['regola', voce.handle]));

  if (errori.length) return { dati: null, errori };
  const { valuta, rotte, immagine_iniziale, diagnostica, lingua } = pagina;
  return { dati: { ...predefinita, valuta, rotte, immagine_iniziale, diagnostica, lingua, passi, opzioni, risoluzioni }, errori };
}

/* ================================================================================================
   CONFIGURAZIONE — lo stato di una sessione
   ------------------------------------------------------------------------------------------------
   `risposte` è l'unico stato: chiave del passo → [chiavi delle opzioni] oppure [testo].
   Tutto il resto si ricalcola nel «quadro» (_calcola), tenuto in cache finché le risposte non cambiano:
     passi      i passi visibili, in ordine, con i rami inseriti dopo il passo della porta
     contesto   chiave → valori, da cui si leggono mostra_se e le regole
     visibili   passo → opzioni visibili
     risposte   le risposte ripulite (solo passi e opzioni visibili)
   e, calcolati alla prima richiesta e tenuti sul quadro stesso:
     scelte     le opzioni scelte (_opzioniScelte)
     possibili  cosa può ancora succedere (_possibili)
     basi       spazio → prodotto configurato (_base)
     linee      le righe della configurazione (_linee)
   Un quadro si calcola anche per risposte ipotetiche (le verifiche delle opzioni): le sue cache valgono per lui.
   ================================================================================================ */

export class Configurazione {
  constructor(modello) {
    this.modello = modello;
    this.risposte = new Map();
    this._passo = null; // passo corrente
    this._indice = 0; // sua posizione, per ritrovarlo se sparisce
  }

  /* ---------------------------------------------------------------- lettura ---------------- */

  passi() {
    return this._quadro().passi;
  }

  principali() {
    return this.passi().filter((p) => p.spazio === this.modello.configuratore);
  }

  scelte(passo) {
    return this._quadro().risposte.get(passo.chiave) || [];
  }

  get corrente() {
    const passi = this.passi();
    if (!passi.includes(this._passo)) this._passo = passi[Math.max(0, Math.min(this._indice, passi.length - 1))] || null;
    this._indice = Math.max(0, passi.indexOf(this._passo));
    return this._passo;
  }

  /* Si entra in un passo solo se tutti quelli prima sono validi. */
  vaiA(passo) {
    const passi = this.passi(), i = passi.indexOf(passo);
    if (i < 0 || !passi.slice(0, i).every((q) => this.valido(q))) return false;
    [this._passo, this._indice] = [passo, i];
    return true;
  }

  /* Un passo obbligatorio le cui opzioni sono tutte inacquistabili non blocca: il problema resta visibile
   * sull'opzione e, se serve, in problemiCarrello(). */
  valido(passo) {
    if (!passo.obbligatorio || passo.tipo === 'riepilogo' || this.scelte(passo).length) return true;
    if (passo.tipo === 'testo' || this._statiOpzioni(passo).some((r) => !r.problema)) return false;
    avvisa(this.modello, false, 'passo obbligatorio senza opzioni acquistabili: non blocca', passo.handle);
    return true;
  }

  /* Le opzioni visibili del passo: { opzione, scelta, problema, foto, prezzo }.
   *   problema: null | { tipo: esaurito | percorso | combinazione, causa? } (vedi _problema)
   *   foto:     la foto di ciò che si sceglie, con la sua `fonte` (vedi _fotoOpzione)
   *   prezzo:   null | { centesimi, stimato, delta }
   *     centesimi  il totale che il cliente vedrebbe dopo il clic: la scelta si simula su una copia, con gli
   *                stessi azzeramenti a valle di una scelta vera (A4), quindi prezzo promesso = prezzo ottenuto
   *     stimato    se quel totale è una stima
   *     delta      a scelta singola: la differenza dall'opzione acquistabile più economica del passo (M4);
   *                a scelta multipla: quanto l'opzione aggiunge al totale attuale (o toglie, se è spuntata)
   * Un'opzione con un problema non ha prezzo. */
  opzioni(passo = this.corrente) {
    if (!passo) return [];
    return this._memo('opzioni' + passo.indice, () => {
      const multipla = passo.tipo === 'multipla', attuale = this.totale();
      const righe = this._statiOpzioni(passo).map((riga) => {
        let prezzo = null;
        if (!riga.problema) {
          const conLei = riga.scelta ? attuale : this._totaleDopo(passo, riga.opzione);
          if (conLei.centesimi != null) {
            prezzo = { centesimi: conLei.centesimi, stimato: conLei.stimato, delta: null };
            if (multipla) {
              const senzaLei = riga.scelta ? this._totaleDopo(passo, riga.opzione, true) : attuale;
              if (senzaLei.centesimi != null) prezzo.delta = conLei.centesimi - senzaLei.centesimi;
            }
          }
        }
        return { ...riga, foto: this.fotoOpzione(passo, riga.opzione), prezzo };
      });
      if (!multipla) {
        const conPrezzo = righe.filter((r) => r.prezzo);
        const piuEconomica = Math.min(...conPrezzo.map((r) => r.prezzo.centesimi));
        for (const r of conPrezzo) r.prezzo.delta = r.prezzo.centesimi - piuEconomica;
      }
      return righe;
    });
  }

  /* Un prodotto configurato per spazio (il principale e i rami con regole): vedi _base. */
  basi() {
    const quadro = this._quadro();
    return this._spaziConProdotto(quadro).map((spazio) => this._base(quadro, spazio));
  }

  /* { centesimi, stimato }: stimato finché una riga non ha la sua variante (l'interfaccia scrive «da»). */
  totale() {
    return this._totale(this._quadro());
  }

  /* L'immagine dell'area visuale, solo da opzioni visibili (UXV-01) e mai di un prodotto non ancora deciso (A5):
   * { url, alt, didascalia, tipo, opzione? } con tipo
   *   attiva    l'opzione indicata dall'interfaccia (passaggio del mouse, fuoco)
   *   scelta    l'opzione scelta al passo
   *   passo     un'opzione del passo, quando non se n'è scelta nessuna
   *   porta     l'opzione che ha aperto il ramo di cui fa parte il passo
   *   prodotto  il prodotto deciso dello spazio (la variante, se univoca)
   *   iniziale  immagine_iniziale dei dati */
  anteprima(passo = this.corrente, attiva) {
    const quadro = this._quadro(), visibili = (passo && quadro.visibili.get(passo)) || [], scelte = passo ? this.scelte(passo) : [];
    const foto = (opzione, tipo) => {
      const f = visibili.includes(opzione) && this._foto(quadro, opzione);
      return f && { ...f, tipo };
    };
    const iniziale = this.modello.immagine_iniziale;
    return (
      foto(attiva, 'attiva') ||
      visibili.filter((o) => scelte.includes(o.chiave)).map((o) => foto(o, 'scelta')).find(Boolean) ||
      (!scelte.length && visibili.map((o) => foto(o, 'passo')).find(Boolean)) ||
      (passo && this._fotoPorta(quadro, passo.spazio)) ||
      (passo && this._fotoProdotto(quadro, passo.spazio)) ||
      (iniziale ? { url: iniziale, alt: '', didascalia: '', tipo: 'iniziale' } : null)
    );
  }

  /* La distinta: le righe (vedi _linee) e le risposte date, nell'ordine del percorso. */
  distinta() {
    const quadro = this._quadro();
    const risposte = [...quadro.risposte].map(([chiave, valori]) => {
      const passo = this._passoVisibile(quadro, chiave);
      return { passo, nome: nomeProprieta(passo), valore: testoRisposta(passo, valori) };
    });
    return { righe: this._linee(quadro), risposte };
  }

  /* Cosa impedisce l'aggiunta al carrello: [{ tipo, passo?, spazio?, opzione?, dimensioni? }]
   *   obbligatorio    un passo obbligatorio senza risposta
   *   nessuna_regola  nessuna regola decide il prodotto dello spazio
   *   combinazione    la variante per le scelte fatte non esiste
   *   indeterminata   C1: restano dimensioni di variante che nessuna scelta ha fissato (`dimensioni`)
   *   esaurito        la variante c'è ma non è acquistabile
   *   vuota           non c'è niente da vendere
   * `passo` è il passo da riaprire per rimediare, quando si sa quale. */
  problemiCarrello() {
    const quadro = this._quadro(), linee = this._linee(quadro);
    const problemi = quadro.passi.filter((p) => !this.valido(p)).map((passo) => ({ tipo: 'obbligatorio', passo }));
    for (const linea of linee) {
      const esito = linea.base || linea.accessorio, variante = linea.variante, libere = esito?.libere || [];
      let tipo = null;
      if (esito && !esito.prodotto) tipo = 'nessuna_regola';
      else if (esito?.stato === 'combinazione' || (!variante && !libere.length)) tipo = 'combinazione';
      else if (!variante) tipo = 'indeterminata';
      else if (!variante.disponibile) tipo = 'esaurito';
      if (!tipo) continue;
      const daRiaprire = linea.passo || this._passoCheVincola(quadro, linea.spazio, libere);
      problemi.push({ tipo, spazio: linea.spazio, passo: daRiaprire, opzione: linea.opzione || null, dimensioni: libere });
    }
    if (!linee.length) problemi.push({ tipo: 'vuota' });
    return problemi;
  }

  /* Le righe per POST /cart/add.js (una sola chiamata), oppure null se c'è un problema.
   * Riga principale per prima; proprietà leggibili sulla riga del loro spazio; su tutte le righe lo stesso
   * _bundle (il configuratore) e lo stesso _config_id (derivato dal codice ?c=). */
  righeCarrello() {
    if (this.problemiCarrello().length) return null;
    const quadro = this._quadro(), linee = this._linee(quadro), proprieta = linee.map(() => ({}));
    for (const [spazio, valori] of this._proprieta(quadro)) {
      for (const linea of this._destinazione(linee, spazio)) Object.assign(proprieta[linee.indexOf(linea)], valori);
    }
    const tecniche = { _config_id: this.idConfigurazione(), _bundle: this.modello.configuratore };
    return linee.map((linea, i) => ({ id: linea.variante.id, quantity: 1, properties: { ...proprieta[i], ...tecniche } }));
  }

  idConfigurazione() {
    return 'cfg_' + fnv1a(this.codice());
  }

  /* Il codice per ?c=: `1!passo*opzione*opzione!passo*testo`. Ogni pezzo è codificato con encodeURIComponent,
   * più `!` e `*`. Nell'URL va messo con URLSearchParams (che lo codifica una seconda volta), vedi la doc §13. */
  codice() {
    const cifra = (testo) =>
      encodeURIComponent(Array.from(String(testo), carattereIntero).join('')).replace(/[!*]/g, (c) => (c === '!' ? '%21' : '%2A'));
    const pezzi = [...this._quadro().risposte].map(([chiave, valori]) => [chiave, ...valori].map(cifra).join('*'));
    return [VERSIONE_CODICE, ...pezzi].join('!');
  }

  /* Rimette le risposte di un codice. null se il codice non è riconosciuto (lo stato non cambia); altrimenti
   * { scartate: [{ chiave, valori }] }: le risposte del codice che non si possono più rimettere (passo o opzione
   * spariti, o diventati incompatibili). I testi passano dalla stessa pulizia di scriviTesto. Il passo corrente
   * diventa il primo non valido. */
  ripristina(codice) {
    if (typeof codice !== 'string' || codice.length > LIMITE_CODICE) return null;
    const [versione, ...pezzi] = codice.split('!');
    if (versione !== VERSIONE_CODICE) return null;
    const lette = new Map();
    for (const pezzo of pezzi) {
      let chiave, valori;
      try {
        [chiave, ...valori] = pezzo.split('*').map(decodeURIComponent);
      } catch {
        continue; // pezzo non decodificabile: si ignora
      }
      // il taglio del passo con quella chiave; se più passi la condividono, il più largo (niente si perde per nessuno)
      const limite = Math.max(0, ...this.modello.passi.filter((p) => p.chiave === chiave).map(limiteTesto)) || LIMITE_TESTO;
      valori = valori.map((valore) => testoLibero(valore, limite)).filter(Boolean);
      if (chiave && valori.length) lette.set(chiave, valori);
    }
    [this.risposte, this._passo, this._indice] = [new Map(lette), null, 0];
    this._invalida();
    this._riconcilia();
    const rimesse = this._quadro().risposte;
    const scartate = [...lette]
      .map(([chiave, valori]) => ({ chiave, valori: valori.filter((v) => !rimesse.get(chiave)?.includes(v)) }))
      .filter((x) => x.valori.length);
    this._passo = this.passi().find((p) => !this.valido(p)) || this.passi().at(-1) || null;
    return { scartate };
  }

  /* «Titolo del passo: risposta», una per riga: per il messaggio d'aiuto (WhatsApp). */
  testoRiassunto() {
    return this.distinta().risposte.map((r) => r.nome + ': ' + r.valore).join('\n');
  }

  /* ---------------------------------------------------------------- per la vetrina ---------- */
  /* Letture al servizio della presentazione: quale foto è onesto mostrare, quali risposte descrivono un
   * prodotto, quanto costa il prodotto di un ramo. Nessuna cambia lo stato. */

  /* Una copia con le stesse risposte e lo stesso passo corrente: per simulare senza toccare lo stato. */
  clona() {
    const copia = new Configurazione(this.modello);
    [copia.risposte, copia._quadroInCache, copia._passo, copia._indice] = [new Map(this.risposte), this._quadroInCache, this._passo, this._indice];
    return copia;
  }

  /* La foto del prodotto di uno spazio: { tipo, url, titolo, variante } oppure null.
   *   tuo      il prodotto è deciso e la sua foto è certa: la variante è univoca, oppure le varianti ancora
   *            compatibili hanno tutte la stessa foto (A5)
   *   esempio  altrimenti la variante più conveniente ancora ottenibile, anche fra prodotti non ancora decisi:
   *            l'interfaccia la presenta come esempio, mai come la scelta del cliente
   * Mai la foto di un'opzione: una mascherina non è «il tuo faro». */
  fotoProdotto(spazio = this.modello.configuratore) {
    const quadro = this._quadro();
    if (!this._spaziConProdotto(quadro).includes(spazio)) return null;
    const base = this._base(quadro, spazio), prodotto = base.prodotto;
    if (prodotto?.varianti && base.stato === 'ok' && base.indici.length) {
      const foto = new Set(base.indici.map((i) => prodotto.varianti.immagine?.[i]));
      const url = (base.indici.length === 1 || foto.size === 1) && fotoDelProdotto(prodotto, base.indici);
      if (url) return { tipo: 'tuo', url, titolo: prodotto.titolo || '', variante: base.variante };
    }
    const esempio = base.esempio || base.sbocco;
    return esempio?.immagine ? { tipo: 'esempio', url: esempio.immagine, titolo: esempio.prodotto || '', variante: esempio } : null;
  }

  /* La foto di ciò che si sceglie con un'opzione visibile del passo: la stessa di opzioni(passo).foto, senza
   * calcolare giudizi e prezzi di tutto il passo (serve, per esempio, a rivedere la scelta fatta a un passo
   * precedente). `opzione` è l'oggetto o la sua chiave; null se l'opzione non è visibile o non ha foto. */
  fotoOpzione(passo, opzione) {
    const quadro = this._quadro(), chiave = opzione?.chiave ?? opzione;
    const visibile = passo && quadro.visibili.get(passo)?.find((o) => o.chiave === chiave);
    return visibile ? this._memo('foto' + passo.indice + '/' + chiave, () => this._fotoOpzione(quadro, passo, visibile)) : null;
  }

  /* Le risposte che compongono il prodotto di uno spazio (R15): quelle che la sua regola legge nella
   * condizione (anche attraverso un `imposta`) e quelle che ne vincolano la variante con la mappa. Le opzioni che
   * vendono qualcosa di loro (variante, accessorio) restano fuori; una risposta che sceglie una dimensione di
   * variante del prodotto invece entra, anche se è un «No, grazie» (la copertura danni con Garanzia=No): è
   * l'interfaccia a darle il contesto, con il nome della proprietà d'ordine. [{ passo, opzione }] nell'ordine del
   * percorso. */
  specifiche(spazio = this.modello.configuratore) {
    const quadro = this._quadro(), chiavi = new Set((this._base(quadro, spazio).prodotto?.quando || []).map(([k]) => k));
    const specifiche = [];
    for (const passo of quadro.passi) {
      for (const chiave of quadro.risposte.get(passo.chiave) || []) {
        const o = this._opzione(passo, chiave);
        if (!o || o.prodotto || o.variante || this._spazioVincolato(o) !== spazio) continue;
        const letta = chiavi.has(minuscolo(passo.chiave)) || o.imposte.some(([k]) => chiavi.has(k));
        if (letta || o.vincoli.length) specifiche.push({ passo, opzione: o });
      }
    }
    return specifiche;
  }

  /* Le foto del prodotto di uno spazio con la variante decisa (R16): prima quella della variante, poi quelle
   * della galleria che non appartengono ad altre varianti (gli altri colori non compaiono). [] finché la
   * variante non è univoca. */
  galleria(spazio = this.modello.configuratore) {
    const base = this._base(this._quadro(), spazio), prodotto = base.prodotto;
    if (!base.variante || !prodotto?.varianti) return [];
    const numeri = prodotto.varianti.immagine || [], sua = numeri[base.indici[0]];
    const altrui = new Set(numeri.filter((n) => n > 0 && n !== sua));
    const foto = [base.variante.immagine, ...(prodotto.immagini || []).filter((_, i) => !altrui.has(i + 1))];
    return [...new Set(foto.filter(Boolean))];
  }

  /* Il prezzo del prodotto dello spazio di un passo dopo ciascuna scelta possibile: nei passi di un prodotto
   * innestato (il faro dentro l'impianto) si confronta il suo prezzo, non il totale (R3h).
   * Map chiave opzione → { centesimi, stimato }; vuota se lo spazio del passo non ha regole proprie. */
  prezziProdotto(passo = this.corrente) {
    if (!passo || !this.modello.spaziConRegole.has(passo.spazio)) return new Map();
    return this._memo('prodotto' + passo.indice, () => {
      const prezzi = new Map();
      for (const riga of this.opzioni(passo)) {
        if (riga.problema) continue;
        const copia = this.clona();
        if (!riga.scelta) copia._scrivi(passo, copia._risposteCon(passo, riga.opzione));
        const linea = copia._linee(copia._quadro()).find((l) => l.base && l.spazio === passo.spazio);
        if (linea?.prezzo != null) prezzi.set(riga.opzione.chiave, { centesimi: linea.prezzo, stimato: linea.stimato });
      }
      return prezzi;
    });
  }

  /* ---------------------------------------------------------------- scrittura -------------- */
  /* Restituiscono null se la scrittura è rifiutata; altrimenti le risposte azzerate di conseguenza (A4):
   * [{ passo, opzioni, testo?, saltato }], saltato = il passo intero è uscito dal percorso. */

  scegli(passo, chiave) {
    const quadro = this._quadro(), opzione = quadro.visibili.get(passo)?.find((o) => o.chiave === chiave);
    if (!opzione || this._verifica(this._precedenti(quadro, passo), passo, opzione)) return null;
    return this._scrivi(passo, this._risposteCon(passo, opzione));
  }

  togli(passo, chiave) {
    return this._scrivi(passo, this.scelte(passo).filter((k) => k !== chiave));
  }

  scriviTesto(passo, testo) {
    return this._scrivi(passo, [testoLibero(testo, limiteTesto(passo))].filter(Boolean));
  }

  /* ================================================================ interno ================== */

  /* Le risposte del passo dopo aver scelto `opzione`: a scelta multipla restano le altre spunte, tranne quelle
   * dello stesso `gruppo` (si escludono a vicenda). */
  _risposteCon(passo, opzione) {
    if (passo.tipo !== 'multipla') return [opzione.chiave];
    const stessoGruppo = (k) => opzione.gruppo && this._opzione(passo, k)?.gruppo === opzione.gruppo;
    return [...this.scelte(passo).filter((k) => k !== opzione.chiave && !stessoGruppo(k)), opzione.chiave];
  }

  _scrivi(passo, valori) {
    const prima = this._quadro();
    this._imposta(passo, valori);
    return this._riconcilia(passo, prima);
  }

  _imposta(passo, valori) {
    if (valori.length) this.risposte.set(passo.chiave, valori);
    else this.risposte.delete(passo.chiave);
    this._invalida();
  }

  _invalida() {
    this._quadroInCache = null;
    this._cache = null;
  }

  _quadro() {
    return (this._quadroInCache ||= this._calcola(this.risposte));
  }

  _memo(chiave, calcola) {
    const cache = (this._cache ||= new Map());
    if (!cache.has(chiave)) cache.set(chiave, calcola());
    return cache.get(chiave);
  }

  /* Il totale dopo aver scelto (o tolto) `opzione`, su una copia della configurazione: lo stato vero non cambia.
   * L'opzione è già stata giudicata acquistabile (_statiOpzioni), quindi la copia scrive senza rigiudicarla; gli
   * azzeramenti a valle sono quelli di una scelta vera. La copia parte dallo stesso quadro (stesse risposte). */
  _totaleDopo(passo, opzione, togli = false) {
    const copia = new Configurazione(this.modello);
    [copia.risposte, copia._quadroInCache] = [new Map(this.risposte), this._quadro()];
    copia._scrivi(passo, togli ? copia.scelte(passo).filter((k) => k !== opzione.chiave) : copia._risposteCon(passo, opzione));
    return copia.totale();
  }

  _opzione(passo, chiave) {
    return passo?.opzioni.find((o) => o.chiave === chiave);
  }

  _passoVisibile(quadro, chiave) {
    return quadro.passi.find((p) => p.chiave === chiave);
  }

  _opzioniScelte(quadro) {
    return (quadro.scelte ||= [...quadro.risposte].flatMap(([chiave, valori]) => {
      const passo = this._passoVisibile(quadro, chiave);
      return valori.map((k) => this._opzione(passo, k)).filter(Boolean);
    }));
  }

  _spazioVincolato(opzione) {
    return spazioVincolato(this.modello, opzione);
  }

  /* Gli spazi che hanno un prodotto configurato: il principale, più i rami aperti che hanno regole. */
  _spaziConProdotto(quadro) {
    const spazi = [...new Set([this.modello.configuratore, ...quadro.passi.map((p) => p.spazio)])];
    return spazi.filter((spazio, i) => i === 0 || this.modello.spaziConRegole.has(spazio));
  }

  /* Le opzioni scelte che aprono il ramo `spazio` (più di una se due porte portano allo stesso ramo). */
  _porte(quadro, spazio) {
    return this._opzioniScelte(quadro).filter((o) => o.apre_ramo === spazio);
  }

  /* Il percorso: in ordine, un passo è visibile se il suo mostra_se vale con le risposte date PRIMA di lui;
   * un'opzione scelta scrive `chiave-del-passo=chiave-opzione` e le coppie di `imposta` nel contesto; se apre un
   * ramo, i passi del ramo si visitano subito dopo. Un ramo si inserisce una volta sola, anche se più porte lo
   * aprono. Le risposte a passi o opzioni non visibili si scartano. */
  _calcola(risposte) {
    const m = this.modello, contesto = new Map(), passi = [], visibili = new Map(), pulite = new Map(), rami = new Set();
    registra(contesto, 'configuratore', m.configuratore);
    const fatte = []; // le opzioni scelte nei passi già visitati: filtrano i passi daVarianti
    const visita = (lista, livello) => {
      for (const passo of lista) {
        if (!condizioneVera(passo.quando, contesto)) continue;
        let opzioni = passo.opzioni.filter((o) => condizioneVera(o.quando, contesto));
        if (passo.daVarianti) {
          const ammesse = opzioniAmmesse(m, passo, contesto, fatte);
          opzioni = opzioni.filter((o) => ammesse.has(o.chiave));
          if (opzioni.length < 2) continue; // niente da scegliere: la dimensione è già decisa (o impossibile)
        }
        passi.push(passo);
        visibili.set(passo, opzioni);
        const date = risposte.get(passo.chiave) || [];
        if (passo.tipo === 'testo' && date[0]) {
          pulite.set(passo.chiave, [date[0]]);
          registra(contesto, passo.chiave, date[0]);
        }
        const scelte = [];
        for (const o of opzioni) {
          if (date.includes(o.chiave) && !scelte.some((s) => o.gruppo && s.gruppo === o.gruppo)) scelte.push(o);
        }
        if (passo.tipo !== 'multipla') scelte.splice(1);
        if (scelte.length) pulite.set(passo.chiave, scelte.map((o) => o.chiave));
        fatte.push(...scelte);
        for (const o of scelte) for (const [k, v] of [[passo.chiave, o.chiave], ...o.imposte]) registra(contesto, k, v);
        for (const o of scelte) {
          if (!o.apre_ramo) continue;
          if (!m.rami[o.apre_ramo]) avvisa(m, true, 'ramo inesistente', o.handle);
          else if (rami.has(o.apre_ramo)) avvisa(m, false, 'più porte verso lo stesso ramo: i passi compaiono una volta', o.apre_ramo);
          else if (livello >= PROFONDITA_RAMI) avvisa(m, true, 'rami annidati oltre il terzo livello: ignorati', o.handle);
          else {
            rami.add(o.apre_ramo);
            visita(m.rami[o.apre_ramo], livello + 1);
          }
        }
      }
    };
    visita(m.principali, 0);
    return { passi, contesto, visibili, risposte: pulite };
  }

  /* Le risposte date ai passi che precedono `passo` nel percorso, nell'ordine del percorso. */
  _precedenti(quadro, passo) {
    const i = quadro.passi.indexOf(passo), prima = i < 0 ? quadro.passi : quadro.passi.slice(0, i);
    return new Map(prima.filter((q) => quadro.risposte.has(q.chiave)).map((q) => [q.chiave, quadro.risposte.get(q.chiave)]));
  }

  _statiOpzioni(passo) {
    return this._memo('stati' + passo.indice, () => {
      const quadro = this._quadro(), scelte = this.scelte(passo);
      return (quadro.visibili.get(passo) || []).map((opzione) => ({
        opzione,
        scelta: scelte.includes(opzione.chiave),
        problema: this._problema(quadro, passo, opzione)
      }));
    });
  }

  /* Perché un'opzione non si può scegliere, giudicando solo le risposte PRECEDENTI (A4):
   *   esaurito      porta solo a varianti non acquistabili; `causa` è la risposta che, cambiata con un'altra
   *                 sceglibile, la libererebbe (vedi _causaEsaurito)
   *   percorso      non porta a niente nemmeno con le sole risposte che servono a vederla (A3)
   *   combinazione  sarebbe possibile, ma una risposta precedente la esclude: `causa` è la prima che lo fa */
  _problema(quadro, passo, opzione) {
    const precedenti = this._precedenti(quadro, passo), tipo = this._verifica(precedenti, passo, opzione);
    if (tipo === 'esaurito') {
      // una variante precisa esaurita lo è comunque: nessuna risposta lo spiega
      const causa = !opzione.variante && (!opzione.prodotto || opzione.vincoli.length) && this._causaEsaurito(quadro, passo, opzione, precedenti);
      return causa ? { tipo, causa } : { tipo };
    }
    if (tipo !== 'combinazione') return tipo ? { tipo } : null;
    const essenziali = this._essenziali(precedenti, passo, opzione);
    const conEssenziali = this._verifica(essenziali, passo, opzione);
    if (conEssenziali === 'combinazione' || conEssenziali === 'percorso') return { tipo: 'percorso' };
    // si rimettono le altre risposte in ordine di percorso: la prima che rende l'opzione impossibile è la causa
    const parziali = new Map(essenziali);
    for (const [chiave, valori] of precedenti) {
      if (essenziali.has(chiave)) continue;
      parziali.set(chiave, valori);
      if (this._verifica(parziali, passo, opzione) !== 'combinazione') continue;
      const causa = this._passoVisibile(quadro, chiave);
      return { tipo, causa: { passo: causa, opzioni: valori.map((k) => this._opzione(causa, k)).filter(Boolean) } };
    }
    return { tipo };
  }

  /* Perché un'opzione è esaurita: la risposta che, sostituita con un'ALTRA opzione sceglibile dello stesso passo,
   * la rende acquistabile. Togliere la risposta non basta: senza «Blu» un faro RGB può tornare disponibile in un
   * colore che non è fra le opzioni, ma nessun colore sceglibile lo libera; cambiare l'anello sì («Esaurito con:
   * Anello RGB»). Si prova dalla risposta più recente, solo sui passi a scelta singola; null se nessuna la spiega.
   * { passo, opzioni }, come la causa di `combinazione`. */
  _causaEsaurito(quadro, passo, opzione, precedenti) {
    const chiavi = [...precedenti.keys()];
    for (let i = chiavi.length - 1; i >= 0; i--) {
      const q = this._passoVisibile(quadro, chiavi[i]), date = precedenti.get(chiavi[i]);
      if (!q || q.tipo === 'testo' || q.tipo === 'multipla') continue;
      const prima = new Map(chiavi.slice(0, i).map((k) => [k, precedenti.get(k)]));
      for (const alternativa of quadro.visibili.get(q) || []) {
        if (date.includes(alternativa.chiave) || this._verifica(prima, q, alternativa)) continue;
        const prova = new Map(precedenti).set(q.chiave, [alternativa.chiave]);
        // l'alternativa deve lasciare l'opzione al suo posto: se la nasconde non la libera (con la targa 50cc la marca
        // KTM non c'è, e «Esaurito con: Moto 125cc» farebbe credere il contrario; 2026-10-03)
        const resta = this._calcola(new Map(prova).set(passo.chiave, [opzione.chiave])).risposte.get(passo.chiave)?.includes(opzione.chiave);
        if (!resta || this._verifica(prova, passo, opzione)) continue;
        return { passo: q, opzioni: date.map((k) => this._opzione(q, k)).filter(Boolean) };
      }
    }
    return null;
  }

  /* Delle risposte precedenti, solo quelle senza cui l'opzione non si vedrebbe più (il passo che la mostra, la
   * porta del suo ramo…). Si prova a togliere una risposta alla volta, dall'ultima alla prima. */
  _essenziali(precedenti, passo, opzione) {
    const visibile = (risposte) =>
      this._calcola(new Map(risposte).set(passo.chiave, [opzione.chiave])).risposte.get(passo.chiave)?.includes(opzione.chiave);
    let essenziali = new Map(precedenti);
    for (const chiave of [...precedenti.keys()].reverse()) {
      const senza = new Map(essenziali);
      senza.delete(chiave);
      if (visibile(senza)) essenziali = senza;
    }
    return essenziali;
  }

  /* Il giudizio su un'opzione, date le risposte `precedenti`: null | esaurito | percorso | combinazione. */
  _verifica(precedenti, passo, opzione) {
    // un'opzione che vende una variante precisa: conta solo la disponibilità
    if (opzione.variante || (opzione.prodotto && !opzione.vincoli.length)) {
      return (opzione.variante || opzione.prodotto).disponibile === false ? 'esaurito' : null;
    }
    const quadro = this._calcola(new Map(precedenti).set(passo.chiave, [opzione.chiave]));
    if (opzione.prodotto) return this._giudicaAccessorio(quadro, opzione);
    const spazio = this._spazioVincolato(opzione);
    // a scelta multipla le spunte non si giudicano una a una, tranne la porta verso un prodotto: il suo ramo sì
    if (passo.tipo === 'multipla') return spazio !== passo.spazio ? this._esitoSpazio(quadro, spazio) : null;
    const esito = this._esitoSpazio(quadro, passo.spazio) || (spazio !== passo.spazio ? this._esitoSpazio(quadro, spazio) : null);
    if (esito) return esito;
    // un passo di un ramo che configura l'accessorio della sua porta (per esempio il colore di un accessorio)
    for (const porta of this._porte(quadro, passo.spazio)) {
      if (!porta.prodotto || !porta.vincoli.length || porta.variante) continue;
      const giudizio = this._giudicaAccessorio(quadro, porta);
      if (giudizio) return giudizio;
    }
    return null;
  }

  /* Un accessorio (prodotto_aggiuntivo con mappa) è impossibile se nessuna variante rispetta la mappa, o se
   * restano dimensioni diverse che nessun passo ancora aperto può fissare (C1); esaurito se nessuna delle
   * varianti rimaste è acquistabile. */
  _giudicaAccessorio(quadro, opzione) {
    const esito = this._accessorio(quadro, opzione);
    if (!esito.indici.length) return 'combinazione';
    if (this._dimensioniBloccate(quadro, opzione.prodotto, esito.indici, opzione.vincoli, new Map()).length) return 'combinazione';
    return esito.disponibile ? null : 'esaurito';
  }

  /* Il prodotto di uno spazio, visto da un'opzione. */
  _esitoSpazio(quadro, spazio) {
    const base = this._base(quadro, spazio);
    if (base.stato === 'fuori') return this._restaDaVendere(quadro) ? null : 'percorso';
    if (base.stato === 'combinazione' || base.senzaUscita?.length) return 'combinazione';
    return (base.stato === 'ok' || base.stato === 'aperta') && !base.disponibile ? 'esaurito' : null;
  }

  /* A3: un percorso fuori dalle regole è accettabile solo se resta qualcosa da vendere, già scelto o ancora
   * possibile: un'opzione con variante, prodotto o prodotto_base, oppure una porta verso uno spazio con una
   * regola ancora raggiungibile. (Il ramo di quella porta non si esplora oltre.) */
  _restaDaVendere(quadro) {
    const possibili = this._possibili(quadro);
    const regolaRaggiungibile = (spazio) => this.modello.regole.some((r) => r.spazio === spazio && possibili.puoValere(r.quando));
    const vende = (o) => o.variante || o.prodotto || o.base || (o.apre_ramo && regolaRaggiungibile(o.apre_ramo));
    return this._opzioniScelte(quadro).some(vende) || [...possibili.opzioni].some(vende);
  }

  /* Vincoli con i segnaposto {chiave} sostituiti dalle risposte date a quel passo (chiave ed etichetta di ogni
   * opzione scelta, o il testo): il valore a catalogo si cerca poi prodotto per prodotto (valoriACatalogo).
   * Un segnaposto il cui passo non ha ancora risposta non vincola niente. */
  _vincoliRisolti(vincoli, quadro) {
    return vincoli.flatMap((vincolo) => {
      if (!vincolo.segnaposto) return [vincolo];
      const passo = quadro.passi.find((q) => minuscolo(q.chiave) === vincolo.segnaposto);
      const date = passo && quadro.risposte.get(passo.chiave);
      if (!date) return [];
      const risposte = passo.tipo === 'testo'
        ? date.map((testo) => ({ chiave: testo }))
        : date.map((k) => {
          const o = this._opzione(passo, k);
          return { chiave: k, etichetta: o && testiPredefiniti(o).etichetta }; // l'etichetta del catalogo, non quella tradotta
        });
      return [{ nome: vincolo.nome, risposte }];
    });
  }

  /* Cosa può ancora succedere dato il quadro: i passi e le opzioni che possono ancora diventare visibili
   * rispondendo ai passi aperti, anche nei rami che le porte ancora possibili aprirebbero (punto fisso).
   * Una coppia chiave=valore è possibile se è già nel contesto o se un'opzione ancora possibile la produce (una
   * chiave-bandiera può ricevere un altro valore da un passo successivo); una chiave che nessun passo né
   * `imposta` produce non vincola. Serve a stimare, a riconoscere i percorsi senza sbocco e le dimensioni di
   * variante che nessuno fisserà più.
   *   { passi, opzioni, chiaviAperte (chiavi dei passi possibili ancora senza risposta), puoValere(condizione) } */
  _possibili(quadro) {
    if (quadro.possibili) return quadro.possibili;
    const m = this.modello, contesto = quadro.contesto;
    const spazi = new Set(quadro.passi.map((p) => p.spazio)), passi = new Set(quadro.passi), opzioni = new Set(), prodotte = new Set();
    const coppiaPossibile = ([chiave, valore]) =>
      contesto.get(chiave)?.has(valore) ||
      prodotte.has(chiave + '=' + valore) ||
      prodotte.has(chiave + '=*') ||
      !m.chiaviProdotte.has(chiave);
    const puoValere = (condizione) => condizione === null || condizione.every(coppiaPossibile);
    for (let prima = -1; prima !== passi.size + opzioni.size; ) {
      prima = passi.size + opzioni.size;
      for (const p of m.passi) if (spazi.has(p.spazio) && puoValere(p.quando)) passi.add(p);
      for (const p of passi) {
        if (quadro.risposte.has(p.chiave)) continue; // un passo già risposto resta com'è
        if (p.tipo === 'testo') prodotte.add(minuscolo(p.chiave) + '=*');
        for (const o of p.opzioni) {
          if (opzioni.has(o) || !puoValere(o.quando)) continue;
          opzioni.add(o);
          if (o.apre_ramo) spazi.add(o.apre_ramo);
          for (const [k, v] of [[p.chiave, o.chiave], ...o.imposte]) prodotte.add(minuscolo(k) + '=' + minuscolo(v));
        }
      }
    }
    const chiaviAperte = new Set([...passi].filter((p) => !quadro.risposte.has(p.chiave)).map((p) => minuscolo(p.chiave)));
    return (quadro.possibili = { passi, opzioni, chiaviAperte, puoValere });
  }

  /* M4: per ogni dimensione di variante dello spazio, i valori che le opzioni ancora possibili sanno produrre
   * (null = qualunque, perché passa da un segnaposto). Una variante che nessuna scelta può ottenere non entra
   * nelle stime; una dimensione assente qui non la fissa più nessuna opzione. */
  _valoriProducibili(quadro, spazio) {
    const valori = new Map();
    for (const o of this._possibili(quadro).opzioni) {
      if (o.prodotto || this._spazioVincolato(o) !== spazio) continue;
      for (const v of o.vincoli) {
        // un segnaposto o un prefisso (`Ktm*`) possono dare più valori: qualunque
        if (v.segnaposto || v.prefissi || valori.get(v.nome) === null) valori.set(v.nome, null);
        else valori.set(v.nome, [...(valori.get(v.nome) || []), ...v.valori]);
      }
    }
    return valori;
  }

  /* C1 al passo: le dimensioni su cui le varianti `indici` differiscono ancora e che nessuna scelta ancora
   * possibile può più fissare: né un'opzione aperta che vincola lo spazio (`producibili`), né un segnaposto delle
   * mappe `vincoli` che aspetta la risposta di un passo aperto. Se ce n'è una, il prodotto non diventerà mai
   * univoco: è un vicolo cieco, e va detto al passo invece che al carrello. */
  _dimensioniBloccate(quadro, prodotto, indici, vincoli, producibili) {
    const M = prodotto.matrice, aperte = this._possibili(quadro).chiaviAperte;
    const inAttesa = new Set(vincoli.filter((v) => v.segnaposto && aperte.has(v.segnaposto)).map((v) => v.nome));
    return colonneLibere(prodotto, indici)
      .filter((j) => !M.nomi[j] || !(producibili.has(M.nomi[j]) || inAttesa.has(M.nomi[j])))
      .map((j) => nomeDimensione(prodotto, j));
  }

  /* Il prodotto configurato di uno spazio: { spazio, stato, prodotto, variante, libere, minimo, disponibile,
   * esempio, indici, senzaUscita }. stato:
   *   ok            prodotto deciso (da prodotto_base o dalla regola soddisfatta)
   *   aperta        non ancora deciso: minimo fra le regole ancora raggiungibili, niente esempio (A5)
   *   combinazione  nessuna variante per le scelte fatte
   *   fuori         nessuna regola raggiungibile e nessuna contraddetta: il percorso è fuori dalle regole
   *   nessuno       lo spazio non ha regole né prodotto_base
   * senzaUscita: le dimensioni che, da qui, nessuna scelta può più fissare (vedi _dimensioniBloccate), quando
   * tutte le varianti raggiungibili ne hanno; vuoto se almeno un prodotto può ancora diventare univoco. */
  _base(quadro, spazio) {
    const memo = (quadro.basi ||= new Map());
    if (memo.has(spazio)) return memo.get(spazio);
    const regole = this.modello.regole.filter((r) => r.spazio === spazio);

    // le mappe delle opzioni scelte che vincolano lo spazio, e il primo prodotto_base
    let prodotto = null;
    const mappe = [];
    for (const o of this._opzioniScelte(quadro)) {
      if (o.prodotto || this._spazioVincolato(o) !== spazio) continue;
      prodotto ||= o.base || null;
      mappe.push(...o.vincoli);
    }
    // senza prodotto_base: la regola soddisfatta più specifica (più condizioni); a parità, la prima
    if (!prodotto) {
      for (const r of regole) {
        if (r.quando.length > (prodotto?.quando.length || 0) && condizioneVera(r.quando, quadro.contesto)) prodotto = r;
      }
    }

    const candidati = prodotto ? [prodotto] : regole.filter((r) => this._possibili(quadro).puoValere(r.quando));
    let esito;
    if (candidati.length) esito = this._esitoCandidati(quadro, spazio, prodotto, candidati, mappe);
    else {
      const contraddetta = regole.some((r) => r.quando.some(([k, v]) => quadro.contesto.get(k) && !quadro.contesto.get(k).has(v)));
      esito = { stato: !regole.length ? 'nessuno' : contraddetta ? 'combinazione' : 'fuori', senzaUscita: [] };
    }
    esito.spazio = spazio;
    memo.set(spazio, esito);
    return esito;
  }

  /* _base, quando c'è almeno un prodotto candidato: il deciso, oppure le regole ancora raggiungibili. */
  _esitoCandidati(quadro, spazio, deciso, candidati, mappe) {
    const producibili = this._valoriProducibili(quadro, spazio);
    let indici = [], raggiungibili = 0, migliore = null, miglioreConUscita = null;
    const senzaUscita = new Set();
    for (const candidato of candidati) {
      const M = candidato.matrice, vincoli = mappe.concat(candidato.vincoli || []); // più la mappa della regola
      const compat = compatibili(candidato, this._vincoliRisolti(vincoli, quadro));
      // per le dimensioni ancora libere contano solo i valori che una scelta può ancora ottenere
      const ottenibile = (i) => M.nomi.every((nome, j) => {
        const valori = producibili.get(nome);
        return compat.vincolate.includes(j) || !valori || valori.includes(M.colonne[j][i]);
      });
      const ottenibili = compat.indici.filter(ottenibile);
      raggiungibili += ottenibili.length;
      migliore = migliorVariante(candidato, ottenibili, migliore);
      if (ottenibili.length) {
        const bloccate = this._dimensioniBloccate(quadro, candidato, ottenibili, vincoli, producibili);
        bloccate.forEach((nome) => senzaUscita.add(nome));
        if (!bloccate.length) miglioreConUscita = migliorVariante(candidato, ottenibili, miglioreConUscita);
      }
      if (candidato === deciso) indici = compat.indici;
    }
    if (senzaUscita.size && !miglioreConUscita) {
      const dove = [spazio, ...senzaUscita];
      avvisa(this.modello, true, 'dimensione di variante che nessun passo può fissare: il prodotto non diventa univoco', dove);
    }
    const esito = {
      stato: raggiungibili ? (deciso ? 'ok' : 'aperta') : 'combinazione',
      ...esitoMatrice(deciso, indici, miglioreConUscita || migliore),
      senzaUscita: miglioreConUscita ? [] : [...senzaUscita]
    };
    if (!deciso) {
      // A5: la base non propone un prodotto che il cliente non ha ancora deciso. La variante più conveniente
      // resta disponibile, non enumerabile, solo per fotoProdotto(), che la dichiara come esempio.
      Object.defineProperty(esito, 'sbocco', { value: esito.esempio, configurable: true });
      esito.esempio = null;
    }
    return esito;
  }

  _accessorio(quadro, opzione) {
    return esitoMatrice(opzione.prodotto, compatibili(opzione.prodotto, this._vincoliRisolti(opzione.vincoli, quadro)).indici);
  }

  /* Le righe della configurazione, nell'ordine del carrello: prima i prodotti configurati (una base per spazio),
   * poi le opzioni con la sola `variante`, poi gli accessori (`prodotto_aggiuntivo`). Ogni riga:
   * { variante (null se non univoca), esempio, prezzo (della variante, o il minimo come stima), stimato,
   *   spazio, principale, base? | passo, opzione, ramo, accessorio? } */
  _linee(quadro) {
    if (quadro.linee) return quadro.linee;
    const riga = (esito, variante, extra) => ({
      variante: variante || null,
      esempio: esito?.esempio || null,
      prezzo: variante ? variante.prezzo : (esito?.minimo ?? null),
      stimato: !variante,
      ...extra
    });
    const basi = this._spaziConProdotto(quadro)
      .map((spazio) => this._base(quadro, spazio))
      .filter((b) => b.stato !== 'nessuno' && b.stato !== 'fuori');
    const linee = basi.map((b) => riga(b, b.variante, { spazio: b.spazio, base: b, principale: true }));
    const accessori = [];
    for (const o of this._opzioniScelte(quadro)) {
      const prodotto = o.prodotto, passo = o.suoPasso;
      const extra = { spazio: passo.spazio, ramo: o.apre_ramo || passo.spazio, passo, opzione: o, principale: !prodotto };
      if (!o.variante && prodotto && o.vincoli.length) {
        extra.accessorio = this._accessorio(quadro, o);
        accessori.push(riga(extra.accessorio, extra.accessorio.variante, extra));
      } else if (o.variante || prodotto) {
        (prodotto ? accessori : linee).push(riga(null, o.variante || variantePredefinita(prodotto), extra));
      }
    }
    return (quadro.linee = linee.concat(accessori));
  }

  /* M21: il prezzo_delta non entra mai (sul piano Basic non avrebbe una riga su cui addebitarsi). */
  _totale(quadro) {
    const linee = this._linee(quadro);
    if (!linee.length || linee.some((l) => l.prezzo == null)) return { centesimi: null, stimato: true };
    return { centesimi: linee.reduce((somma, l) => somma + l.prezzo, 0), stimato: linee.some((l) => l.stimato) };
  }

  /* La foto di un'opzione: la sua immagine, altrimenti quella della variante che vende, altrimenti quella
   * dell'accessorio (della variante solo se è già univoca, vedi fotoDelProdotto). */
  _foto(quadro, opzione) {
    if (!opzione) return null;
    const accessorio = opzione.prodotto && !opzione.variante && opzione.vincoli.length && this._accessorio(quadro, opzione);
    const url = opzione.immagine?.url
      || opzione.variante?.immagine
      || (accessorio && fotoDelProdotto(opzione.prodotto, accessorio.indici))
      || opzione.prodotto?.immagine;
    const testo = opzione.immagine?.alt || opzione.etichetta || '';
    return url ? { url, alt: testo, didascalia: opzione.etichetta || '', tipo: 'opzione', opzione } : null;
  }

  /* La foto di ciò che il cliente sceglie con `opzione` (sulla tessera e nella lastra): { url, alt, didascalia,
   * tipo: 'opzione', opzione, fonte, titolo, certa } oppure null. Nessun nome nel codice: conta solo da dove i dati
   * permettono di prenderla. `fonte`, in ordine di precedenza:
   *   immagine  il campo `immagine` dell'opzione
   *   variante  la variante che l'opzione vende
   *   prodotto  l'accessorio che vende (prodotto_aggiuntivo): come _foto, la sua variante se è già decisa o se le
   *             varianti rimaste hanno la stessa foto, altrimenti la foto principale del prodotto (A5)
   *   porta     l'opzione decide con un segnaposto {chiave del suo passo} la variante di un accessorio: quello della
   *             porta che apre il suo ramo (il colore del portabatteria), oppure quello di un'opzione del suo stesso
   *             spazio (la filettatura dell'idrostop). Si simula la scelta e si guarda la variante che ne esce
   *   spazio    l'opzione vincola con la sua mappa_variante il prodotto del suo spazio, o di quello che apre (il colore
   *             del cablaggio, il corpo del faro), oppure ne decide la regola (decideRegola: il design del faro): si
   *             simula la scelta e, se il prodotto è deciso, si guarda la variante
   *   esempio   come spazio, ma il prodotto non è ancora deciso (la porta verso un configuratore innestato, il design
   *             prima dei LED): la variante più conveniente ancora ottenibile, sempre con certa=false
   * La simulazione parte dalle risposte attuali, comprese le successive: se il cliente ha già detto «con l'AirTag»,
   * i colori si vedono con l'AirTag. `titolo` è il prodotto in foto. `certa`: vedi fotoDiVarianti. */
  _fotoOpzione(quadro, passo, opzione) {
    const foto = (url, fonte, titolo, certa = true) => url
      ? { url, alt: opzione.immagine?.alt || opzione.etichetta || '', didascalia: opzione.etichetta || '', tipo: 'opzione', opzione, fonte, titolo: titolo || '', certa }
      : null;
    if (opzione.immagine?.url) return foto(opzione.immagine.url, 'immagine', '');
    if (opzione.variante) return foto(opzione.variante.immagine, 'variante', opzione.variante.prodotto);
    if (opzione.prodotto) {
      const prodotto = opzione.prodotto;
      if (!opzione.vincoli.length) return foto(prodotto.immagine, 'prodotto', prodotto.titolo);
      const indici = this._accessorio(quadro, opzione).indici, stessa = new Set(indici.map((i) => prodotto.varianti.immagine?.[i])).size === 1;
      return foto(stessa ? fotoDelProdotto(prodotto, indici) : prodotto.immagine, 'prodotto', prodotto.titolo, stessa);
    }
    const segnaposto = minuscolo(passo.chiave);
    const vincola = opzione.vincoli.length > 0 || decideRegola(this.modello, opzione);
    if (!vincola && !passiRichiamati(this.modello).has(segnaposto)) return null; // niente da simulare
    const sim = this._calcola(new Map(quadro.risposte).set(passo.chiave, this._risposteCon(passo, opzione)));

    // porta: gli accessori che richiamano questo passo con un segnaposto, prima quelli delle porte del ramo
    const richiama = (o) => o.prodotto && !o.variante && o.vincoli.some((v) => v.segnaposto === segnaposto);
    const delloSpazio = sim.passi.filter((q) => q.spazio === passo.spazio).flatMap((q) => sim.visibili.get(q) || []);
    const scelte = new Set(this._opzioniScelte(sim));
    const accessori = [...this._porte(sim, passo.spazio), ...delloSpazio.filter((o) => scelte.has(o)), ...delloSpazio];
    for (const o of new Set(accessori.filter(richiama))) {
      const indici = this._accessorio(sim, o).indici;
      if (!indici.length) continue;
      const f = fotoDiVarianti(o.prodotto, indici);
      if (f.url) return foto(f.url, 'porta', o.prodotto.titolo, f.certa);
    }

    // spazio: il prodotto che l'opzione vincola con la sua mappa o di cui decide la regola (il suo spazio, o quello che apre)
    if (!vincola) return null;
    const spazio = this._spazioVincolato(opzione);
    if (!this._spaziConProdotto(sim).includes(spazio)) return null;
    const base = this._base(sim, spazio);
    if (base.stato === 'ok' && base.indici.length) {
      const f = fotoDiVarianti(base.prodotto, base.indici);
      return foto(f.url, 'spazio', base.prodotto.titolo, f.certa);
    }
    // prodotto non ancora deciso (una porta verso un configuratore innestato, 2026-09-25 su richiesta dell'utente):
    // la variante più conveniente ancora ottenibile con le risposte date, mai certa: l'interfaccia la dice «Configurabile»
    const esempio = base.esempio || base.sbocco;
    return esempio?.immagine ? foto(esempio.immagine, 'esempio', esempio.prodotto, false) : null;
  }

  _fotoPorta(quadro, spazio) {
    const foto = this._foto(quadro, this._porte(quadro, spazio)[0]);
    return foto && { ...foto, tipo: 'porta' };
  }

  /* Solo per un prodotto deciso (stato ok), mai per uno ancora da decidere (A5). */
  _fotoProdotto(quadro, spazio) {
    if (!this._spaziConProdotto(quadro).includes(spazio)) return null;
    const base = this._base(quadro, spazio), prodotto = base.prodotto;
    if (!prodotto || base.stato !== 'ok') return null;
    const url = fotoDelProdotto(prodotto, base.indici);
    return url ? { url, alt: prodotto.titolo || '', didascalia: prodotto.titolo || '', tipo: 'prodotto' } : null;
  }

  /* MOT-15: il primo passo visibile con un'opzione (senza prodotto proprio) che vincola lo spazio su una delle
   * dimensioni `libere` (o su una qualunque, se non ce ne sono): è quello da riaprire. */
  _passoCheVincola(quadro, spazio, libere) {
    const sulleLibere = (vincolo) => !libere.length || libere.some((nome) => minuscolo(nome) === vincolo.nome);
    const vincola = (o) => !o.prodotto && this._spazioVincolato(o) === spazio && o.vincoli.some(sulleLibere);
    return quadro.passi.find((q) => q.opzioni.some(vincola)) || null;
  }

  /* M6: le risposte diventano proprietà di riga, raggruppate per spazio, con il nome del passo (etichetta_ordine,
   * o il titolo). Un'opzione che produce righe (vende, o apre un ramo) non diventa proprietà: la sua riga dice
   * già tutto. I passi che vendono restano fuori per intero (niente «No, grazie»), a meno che l'admin non gli
   * dia un'etichetta_ordine: allora vanno nell'ordine le loro risposte che non vendono (per esempio «Statore luci:
   * La mia moto non è in elenco»). I passi saltati non ci sono: non hanno risposte. */
  _proprieta(quadro) {
    const perSpazio = new Map();
    for (const [chiave, valori] of quadro.risposte) {
      const passo = this._passoVisibile(quadro, chiave);
      if (passo.opzioni.some(produceRighe) && !testiPredefiniti(passo).etichetta_ordine) continue;
      const restano = passo.tipo === 'testo' ? valori : valori.filter((k) => !produceRighe(this._opzione(passo, k)));
      if (!restano.length) continue;
      if (!perSpazio.has(passo.spazio)) perSpazio.set(passo.spazio, {});
      perSpazio.get(passo.spazio)[nomeProprieta(passo, true)] = testoRisposta(passo, restano, true);
    }
    return perSpazio;
  }

  /* Su quali righe vanno le risposte di uno spazio: sulla riga del suo prodotto; per un ramo senza prodotto
   * proprio, sulle righe dell'accessorio che il ramo configura; altrimenti sulla prima riga. */
  _destinazione(linee, spazio) {
    const proprie = linee.filter((l) => l.spazio === spazio && l.principale);
    if (proprie.length) return proprie.slice(0, 1);
    const delRamo = spazio !== this.modello.configuratore ? linee.filter((l) => l.ramo === spazio && !l.principale) : [];
    return delRamo.length ? delRamo : linee.slice(0, 1);
  }

  /* A4: dopo una modifica, toglie le risposte che non valgono più, dal passo modificato in giù, finché ce ne
   * sono: quelle a passi o opzioni diventati invisibili, e quelle che con le risposte precedenti non si
   * potrebbero più scegliere. Restituisce ciò che ha tolto (vedi «scrittura»). */
  _riconcilia(da, prima) {
    const azzerate = new Map();
    const segna = (passo, valori, saltato) => {
      const opzioni = valori.map((k) => this._opzione(passo, k)).filter(Boolean);
      const testo = passo.tipo === 'testo' ? valori[0] : undefined;
      if (!opzioni.length && testo === undefined) return;
      if (!azzerate.has(passo)) azzerate.set(passo, { passo, opzioni: [], saltato });
      const voce = azzerate.get(passo);
      voce.saltato ||= saltato; // tolte prima alcune opzioni, poi è uscito il passo intero
      voce.opzioni.push(...opzioni);
      if (testo !== undefined) voce.testo = testo;
    };
    for (;;) {
      // 1. le risposte che il percorso ha scartato (passi o opzioni non più visibili)
      const quadro = this._calcola(this.risposte);
      for (const [chiave, valori] of this.risposte) {
        const passo = this._passoVisibile(quadro, chiave);
        if (passo) segna(passo, valori.filter((v) => !quadro.risposte.get(chiave)?.includes(v)), false);
        else if (prima && this._passoVisibile(prima, chiave)) segna(this._passoVisibile(prima, chiave), valori, true);
      }
      this.risposte = new Map(quadro.risposte);
      [this._quadroInCache, this._cache] = [quadro, null];
      // 2. dopo il passo `da`, la prima risposta che con le risposte precedenti non si potrebbe più dare: si
      //    toglie e si ricomincia da lì, perché toglierla può cambiare i passi successivi
      const guasto = this._primaRispostaImpossibile(quadro, da);
      if (!guasto) break;
      segna(guasto.passo, guasto.escluse, false);
      this._imposta(guasto.passo, this.scelte(guasto.passo).filter((k) => !guasto.escluse.includes(k)));
      da = guasto.passo;
    }
    return [...azzerate.values()];
  }

  /* Le risposte al passo `da` non si ricontrollano: le risposte prima di lui non sono cambiate, quindi il giudizio
   * sulle sue opzioni è quello dato quando sono state scelte. Senza `da` (ripristina) si controlla tutto. */
  _primaRispostaImpossibile(quadro, da) {
    for (const passo of quadro.passi.slice(quadro.passi.indexOf(da) + 1)) {
      const valori = passo.tipo === 'testo' ? [] : quadro.risposte.get(passo.chiave) || [];
      if (!valori.length) continue;
      const precedenti = this._precedenti(quadro, passo);
      const escluse = valori.filter((k) => this._verifica(precedenti, passo, this._opzione(passo, k)));
      if (escluse.length) return { passo, escluse };
    }
    return null;
  }
}
